import moment from 'moment';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import {
  AMOUNT_QUESTION,
  EXCESS_QUESTION,
  FREQUENCY_QUESTION,
  NAME_FIELD,
  POLICY_PROVIDER_QUESTION,
  PREMIUM_QUESTION,
  REGO_FIELD,
  ADDRESS_FIELD,
  RENEWAL_FIELD,
  POLICY_SUB_TYPE_FIELD,
  POLICY_NUMBER_QUESTION,
  LIFE_PREMIUM_COVER_FIELD,
  LIFE_NAMES_FIELD,
  OTHER_NOTES_FIELD,
  PAYMENT_METHOD_QUESTION,
  LIFE_INSURED_COVERS,
  LIFE_FIELD_MAP,
  AMOUNT_CONTENTS_QUESTION,
  AMOUNT_HOME_QUESTION,
  EXCESS_CONTENTS_QUESTION,
  EXCESS_HOME_QUESTION,
  PAYOUT_FIELDS,
  TRAVEL_END_DATE_FIELD,
  TRAVEL_START_DATE_FIELD,
  COPAYMENT_FIELD,
  PET_NAME_FIELD,
  VESSEL_FIELD,
  COVER_LIMIT_QUESTION,
} from 'constants/policy-details';
import {
  convertToDateOnlyString,
  getPolicyTypeByName,
  prettifyNumber,
  removeEmptyValues,
  toDropdownOptions,
} from 'helpers';
import { FieldType, IPolicy } from 'types';
import { PolicyWordingDocs, ProviderNames } from 'constants/policy';

export const getFieldByPolicyType = (policyType: string) => {
  switch (policyType) {
    case INSURANCE_TYPES_NAMES.MBI:
      return [REGO_FIELD];
    case INSURANCE_TYPES_NAMES.MOTORCYCLE:
    case INSURANCE_TYPES_NAMES.VEHICLE:
    case INSURANCE_TYPES_NAMES.RV:
      return [REGO_FIELD, ADDRESS_FIELD];
    case INSURANCE_TYPES_NAMES.CONTENT:
    case INSURANCE_TYPES_NAMES.HOME:
    case INSURANCE_TYPES_NAMES.HOME_CONTENTS:
    case INSURANCE_TYPES_NAMES.LANDLORD:
    case INSURANCE_TYPES_NAMES.RENTERS:
      return [R.assoc('required', true, ADDRESS_FIELD)];
    case INSURANCE_TYPES_NAMES.PET:
      return [PET_NAME_FIELD];
    case INSURANCE_TYPES_NAMES.BOAT:
      return [VESSEL_FIELD];
    case INSURANCE_TYPES_NAMES.LIFE:
      return [LIFE_NAMES_FIELD];
    default:
      return [NAME_FIELD];
  }
};

const mergeOption = (field, options) => {
  return { ...field, options: R.propOr([], field.key, options) };
};

export const getLifeDetailsFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
      options,
    ),
    RENEWAL_FIELD,
    POLICY_NUMBER_QUESTION,
    mergeOption(LIFE_PREMIUM_COVER_FIELD, {
      premiumCovers: toDropdownOptions(policy.insuredNames),
    }),
  ];

  const rightQuestions = [
    { ...LIFE_NAMES_FIELD, options: toDropdownOptions(policy.insuredNames) },
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],

    ...policy.insuredNames.reduce((acc, name, idx) => {
      acc.push({
        ...LIFE_INSURED_COVERS,
        key: `${LIFE_INSURED_COVERS.key}_${idx}`,
        label: `<b>${name}</b> - ${LIFE_INSURED_COVERS.label}`,
        options: options.policyType,
      });

      policy[`${LIFE_INSURED_COVERS.key}_${idx}`]?.forEach((pt) => {
        const fields = LIFE_FIELD_MAP[pt] || LIFE_FIELD_MAP.default;
        fields.forEach((f) => {
          acc.push({
            options: options[f.key],
            ...f,
            key: `${pt}_${f.key}_${idx}`,
            label: `${pt} ${f.label}`,
          });
        });
      });

      return acc;
    }, []),

    PAYMENT_METHOD_QUESTION,
  ];

  return [leftQuestions, rightQuestions];
};

export const getPayoutFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
      options,
    ),
    RENEWAL_FIELD,
    ...getFieldByPolicyType(policy.policyType),
    mergeOption(POLICY_SUB_TYPE_FIELD, options),
    POLICY_NUMBER_QUESTION,
    PAYMENT_METHOD_QUESTION,
  ];

  const rightQuestions = [
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],
    AMOUNT_QUESTION,
    mergeOption(EXCESS_QUESTION, options),
    ...PAYOUT_FIELDS.map(R.assoc('hideDisabled', false)),
  ];

  return [leftQuestions, rightQuestions];
};

export const getHomeContentsFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
      options,
    ),
    RENEWAL_FIELD,
    ...getFieldByPolicyType(policy.policyType),
    mergeOption(POLICY_SUB_TYPE_FIELD, options),
    POLICY_NUMBER_QUESTION,
    PAYMENT_METHOD_QUESTION,
  ];

  const rightQuestions = [
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],
    AMOUNT_HOME_QUESTION,
    mergeOption(EXCESS_HOME_QUESTION, {
      [EXCESS_HOME_QUESTION.key]: options.excessHome,
    }),
    AMOUNT_CONTENTS_QUESTION,
    mergeOption(EXCESS_CONTENTS_QUESTION, {
      [EXCESS_CONTENTS_QUESTION.key]: options.excessContents,
    }),
  ];

  return [leftQuestions, rightQuestions];
};

export const getPetFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
      options,
    ),
    RENEWAL_FIELD,
    ...getFieldByPolicyType(policy.policyType),
    mergeOption(POLICY_SUB_TYPE_FIELD, options),
    POLICY_NUMBER_QUESTION,
  ];

  const rightQuestions = [
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],
    AMOUNT_QUESTION,
    mergeOption(EXCESS_QUESTION, options),
    COPAYMENT_FIELD,
    PAYMENT_METHOD_QUESTION,
  ];

  return [leftQuestions, rightQuestions];
};

export const getTravelFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
      options,
    ),
    TRAVEL_START_DATE_FIELD,
    TRAVEL_END_DATE_FIELD,
    ...getFieldByPolicyType(policy.policyType),
    mergeOption(POLICY_SUB_TYPE_FIELD, options),
  ];

  const rightQuestions = [
    POLICY_NUMBER_QUESTION,
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],
    AMOUNT_QUESTION,
    mergeOption(EXCESS_QUESTION, options),
    PAYMENT_METHOD_QUESTION,
  ];

  return [leftQuestions, rightQuestions];
};

export const getQ4eFields = (policy: IPolicy, options) => {
  const leftQuestions = [
    mergeOption(
      {
        ...POLICY_PROVIDER_QUESTION,
        hideDisabled: false,
        label: 'Provider',
      },
      options,
    ),
  ];
  const rightQuestions = [
    mergeOption(
      {
        ...POLICY_SUB_TYPE_FIELD,
        defaultValue: `${policy.policyType} insurance`,
      },
      options,
    ),
  ];

  if (policy.excess) {
    leftQuestions.push(mergeOption(EXCESS_QUESTION, options));
  }

  return [leftQuestions, rightQuestions];
};

export const getDefaultDetailsFields = (policy: IPolicy, options) => {
  let leftQuestions = [
    RENEWAL_FIELD,
    ...getFieldByPolicyType(policy.policyType),
    mergeOption(POLICY_SUB_TYPE_FIELD, options),
    POLICY_NUMBER_QUESTION,
  ];

  const rightQuestions = [
    [PREMIUM_QUESTION, mergeOption(FREQUENCY_QUESTION, options)],
    AMOUNT_QUESTION,
    mergeOption(EXCESS_QUESTION, options),
    PAYMENT_METHOD_QUESTION,
  ];

  if (policy.policyType === INSURANCE_TYPES_NAMES.OTHER) {
    rightQuestions.push(OTHER_NOTES_FIELD);
  }

  // Balance the left and right fields
  const providerQuestion = mergeOption(
    { ...POLICY_PROVIDER_QUESTION, label: 'Policy provider' },
    options,
  );
  leftQuestions = [providerQuestion, ...leftQuestions];

  return [leftQuestions, rightQuestions];
};

export const getVehicleFields = (policy: IPolicy, options) => {
  const fields = getDefaultDetailsFields(policy, options);

  // Hide insured amount if purchased from Protecta and third party
  if (
    isProtectaPurchased(policy) &&
    policy.policySubTypeId ===
    'policy-sub-type:f00cd0b0-285e-11ea-997c-f329a43eae81'
  ) {
    const right = R.reject(R.propEq('key', AMOUNT_QUESTION.key), fields[1]);
    return [fields[0], right];
  }

  return fields;
};

export const getMbiFields = (policy: IPolicy, options) => {
  const [left, right] = getDefaultDetailsFields(policy, options);

  const updatedLeft = left.map((field) => {
    if (field.key === RENEWAL_FIELD.key) {
      return TRAVEL_END_DATE_FIELD;
    }
    return field;
  });

  const updatedRight = right.map((field) => {
    if (field.key === AMOUNT_QUESTION.key) {
      return COVER_LIMIT_QUESTION;
    }
    return field;
  });

  return [updatedLeft, updatedRight];
};

export const getNames = R.map(R.prop('name'));

export const getDefaultPolicyDetailsValues = (policy: IPolicy) => {
  const { insured = [], policyType } = policy;
  if (policyType === INSURANCE_TYPES_NAMES.LIFE) {
    return {
      ...policy,
      insuredNames: getNames(insured),
      premiumCovers: getNames(insured.filter(R.prop('premiumCover'))),
      ...insured.reduce((acc, ins, idx) => {
        if (ins?.additionalCovers?.length > 0) {
          const policyTypes = ins.additionalCovers.map(R.prop('policyType'));
          acc[`${LIFE_INSURED_COVERS.key}_${idx}`] = policyTypes;
          ins.additionalCovers.forEach((cover) => {
            const pt = cover.policyType;
            const fields = LIFE_FIELD_MAP[pt] || LIFE_FIELD_MAP.default;
            fields.forEach((f) => {
              acc[`${pt}_${f.key}_${idx}`] = cover[f.key];
            });
          });
        }
        return acc;
      }, {}),
    };
  }
  return policy;
};

export const getLifeInsuredFromForm = (formValues: any) => {
  const { insuredNames, premiumCovers } = formValues;
  return insuredNames.map((name, order) => {
    return {
      order,
      name,
      premiumCover: !!premiumCovers.find(R.equals(name)),
      additionalCovers: R.propOr(
        [],
        `${LIFE_INSURED_COVERS.key}_${order}`,
        formValues,
      ).reduce((acc, pt: string) => {
        const policyType = getPolicyTypeByName(pt);
        if (policyType) {
          acc.push(
            removeEmptyValues({
              policyTypeId: policyType.id,
              policyType: pt,
              insuredAmount: formValues[`${pt}_insuredAmount_${order}`],
              excess: formValues[`${pt}_excess_${order}`],
              payoutBenefitPeriod:
                formValues[`${pt}_payoutBenefitPeriod_${order}`],
              payoutWaitPeriod: formValues[`${pt}_payoutWaitPeriod_${order}`],
              payoutFrequency: formValues[`${pt}_payoutFrequency_${order}`],
            }),
          );
        }
        return acc;
      }, []),
    };
  });
};

// Return true when there is no filter or when data satisfies filter
const isEmployerPolicy = (filter, data) => !filter || R.whereEq(filter, data);

export const getEmployerPolicyDetails = (policies, user) => {
  if (policies) {
    return policies.reduce((acc, p) => {
      if (isEmployerPolicy(p.filter, user) && p.policyDetails) {
        acc[p.policyTypeName] = p.policyDetails;
      }
      return acc;
    }, {});
  }
  return null;
};

export const getEmployerPolicyDocs = (policies = [], user) => {
  return policies.reduce((acc, p) => {
    if (isEmployerPolicy(p.filter, user) && p.policyDetails?.documents) {
      return [...acc, ...p.policyDetails.documents];
    }
    return acc;
  }, []);
};

export const getEmployerPolicyDisclaimer = (policies = [], user) => {
  return policies.reduce((acc, p) => {
    if (isEmployerPolicy(p.filter, user) && p.disclaimer) {
      acc[p.policyTypeName] = p.disclaimer;
    }
    return acc;
  }, {});
};

export const getEmployerPolicyGrouped = (policies = [], user) => {
  return policies.reduce((acc, p) => {
    if (isEmployerPolicy(p.filter, user)) {
      acc[p.policyTypeName] = p;
    }
    return acc;
  }, {});
};

export const getOtherDocs = (policy, employerPolicies = [], user) => {
  const employerDocs = getEmployerPolicyDocs(employerPolicies, user);

  if (
    isProtectaPurchased(policy) ||
    isProvidentPurchased(policy) ||
    isAutosurePurchased(policy)
  ) {
    const docs = R.pathOr(
      [],
      [policy.policyType, policy.policyProviderName],
      PolicyWordingDocs,
    );

    if (policy.policyProviderName === ProviderNames.AUTOSURE) {
      return docs.filter(
        (doc) => doc.policySubType === policy.policySubTypeName,
      );
    }

    return docs;
  }

  return employerDocs;
};

export const getValueByType = (field, value) => {
  if (!value) {
    return field.defaultValue || value;
  }

  if (Array.isArray(value)) {
    return value;
  }

  switch (field.type) {
    case FieldType.Birthday:
    case FieldType.Date:
      return convertToDateOnlyString(value, 'DD/MM/YYYY');
    case FieldType.Number:
      return `$${prettifyNumber(value)}`;
    case FieldType.Select:
      const found = field.options.find(R.propEq('value', value));
      return (
        found?.text ||
        (field.creatable
          ? `${field.prefix || ''}${prettifyNumber(value)}`
          : field.defaultValue || '')
      );
    default:
      return value;
  }
};

export const isRenewalAttachment = (renewal, fileName) => {
  if (renewal) {
    return !!renewal?.attachments?.find(R.propEq('fileName', fileName));
  }

  return false;
};

export const isPolicyPurchased = (policy: IPolicy) => {
  const purchaseDate = policy.purchaseDate;
  const vehiclePurchaseDate = R.path(['vehicle', 'purchaseDate'], policy);
  const purchasedAt = policy.purchasedAt;
  return !!(purchaseDate || vehiclePurchaseDate || purchasedAt);
};

export const isProtectaPurchased = (policy: IPolicy) => {
  const purchaseDate = R.path(['vehicle', 'purchaseDate'], policy);
  // We only care if policy is within a year of purchase
  return (
    purchaseDate && policy.policyProviderName === ProviderNames.PROTECTA_DB_NAME
  );
};

export const isProvidentPurchased = (policy: IPolicy) => {
  const purchaseDate = policy.purchaseDate;
  return purchaseDate && policy.policyProviderName === ProviderNames.PROVIDENT;
};

export const isAutosurePurchased = (policy: IPolicy) => {
  const purchaseDate = policy.purchaseDate;
  return purchaseDate && policy.policyProviderName === ProviderNames.AUTOSURE;
};

export const isProtectaPurchasedWithinAYear = (policy: IPolicy) => {
  const purchaseDate = R.path(['vehicle', 'purchaseDate'], policy);
  return (
    isProtectaPurchased(policy) &&
    moment().isBefore(moment(purchaseDate).add(1, 'y'))
  );
};

export const isMbiPurchased = (policy) => {
  return (
    policy?.policyType === INSURANCE_TYPES_NAMES.MBI && !!policy?.purchasedAt
  );
};

export const isProvidentPurchasedWithinAYear = (policy: IPolicy) => {
  const purchaseDate = policy.purchaseDate;
  return (
    isProvidentPurchased(policy) &&
    moment().isBefore(moment(purchaseDate).add(1, 'y'))
  );
};

export const isAutosurePurchasedWithinAYear = (policy: IPolicy) => {
  const purchaseDate = policy.purchaseDate;
  return (
    isAutosurePurchased(policy) &&
    moment().isBefore(moment(purchaseDate).add(1, 'y'))
  );
};

export const isPolicyEditable = (policy, showRenewal) => {
  if (
    isMbiPurchased(policy) ||
    isProtectaPurchasedWithinAYear(policy) ||
    isProvidentPurchasedWithinAYear(policy) ||
    isAutosurePurchasedWithinAYear(policy)
  ) {
    return false;
  }

  // Show edit for partner purchased policies only after renewal
  return showRenewal || policy.policyRenewalAdded;
};
